import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Upload, User, FileText, Eye } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const ROLE_LABELS = { profesor: "Profesor", psicologo: "Psicólogo", administrativo: "Administrativo" };

function Field({ label, children }) {
    return (
        <div>
            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">{label}</span>
            {children}
        </div>
    );
}

export default function TrabajadorDetalle() {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const { user: me } = useAuth();
    const canEdit = me?.role === "administrativo" || me?.role === "ejecutivo";

    const [staff, setStaff] = useState(null);
    const [allStaff, setAllStaff] = useState([]);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [{ data: person }, { data: list }] = await Promise.all([
                api.get(`/users/${staffId}`),
                api.get("/users", { params: { role: "profesor,psicologo,administrativo" } }),
            ]);
            setStaff(person);
            setForm({ name: person.name, cargo: person.cargo || "", fecha_nacimiento: person.fecha_nacimiento || "", jefe_id: person.jefe_id || "" });
            setAllStaff((list || []).filter((s) => s.id !== staffId));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando trabajador");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, [staffId]);

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/users/${staffId}`, {
                name: form.name,
                cargo: form.cargo || null,
                fecha_nacimiento: form.fecha_nacimiento || null,
                jefe_id: form.jefe_id || null,
            });
            toast.success("Guardado correctamente");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando");
        } finally {
            setSaving(false);
        }
    }

    async function uploadPhoto(file) {
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/users/${staffId}`, { profile_photo_id: data.id });
            toast.success("Foto subida");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo la foto");
        }
    }

    async function uploadCV(file) {
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/users/${staffId}`, { cv_file_id: data.id });
            toast.success("CV subido");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo el CV");
        }
    }

    if (loading) return <div className="p-10 text-edu-muted">Cargando…</div>;
    if (!staff) return <div className="p-10 text-edu-muted">No se pudo cargar.</div>;

    const jefe = allStaff.find((s) => s.id === staff.jefe_id);

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-edu-body hover:text-edu-navy mb-6">
                <ArrowLeft size={16} /> Volver
            </button>

            <div className="edu-card p-6 mb-6 flex items-center gap-4">
                {staff.profile_photo_id ? (
                    <img src={fileUrl(staff.profile_photo_id)} alt={staff.name} className="w-16 h-16 rounded-full object-cover border border-edu" />
                ) : (
                    <div className="w-16 h-16 rounded-full bg-edu-soft flex items-center justify-center border border-edu">
                        <User size={24} className="text-edu-muted" />
                    </div>
                )}
                <div className="flex-1">
                    <p className="text-xs text-edu-muted uppercase tracking-widest font-semibold">Hoja de vida</p>
                    <h1 className="text-xl font-semibold">{staff.name}</h1>
                    <p className="text-xs text-edu-muted">{staff.cargo || ROLE_LABELS[staff.role] || staff.role}</p>
                </div>
                {canEdit && (
                    <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft cursor-pointer">
                        <Upload size={12} /> Subir foto
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} />
                    </label>
                )}
            </div>

            {!canEdit && (
                <p className="text-xs text-edu-muted bg-edu-soft rounded-xl p-3 mb-4">Solo lectura — la edición es exclusiva del personal administrativo.</p>
            )}

            <div className="edu-card p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Nombre">
                        <input disabled={!canEdit} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                    </Field>
                    <Field label="Cargo">
                        <input disabled={!canEdit} value={form.cargo || ""} onChange={(e) => setForm({ ...form, cargo: e.target.value })} className="edu-input w-full disabled:opacity-60" placeholder="Ej: Profesor de Matemática" />
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Fecha de nacimiento">
                        <input type="date" disabled={!canEdit} value={form.fecha_nacimiento || ""} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} className="edu-input w-full disabled:opacity-60" />
                    </Field>
                    <Field label="Jefe">
                        <select disabled={!canEdit} value={form.jefe_id || ""} onChange={(e) => setForm({ ...form, jefe_id: e.target.value })} className="edu-input w-full disabled:opacity-60">
                            <option value="">—</option>
                            {allStaff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </Field>
                </div>
                {!canEdit && jefe && (
                    <p className="text-xs text-edu-muted">Jefe actual: {jefe.name}</p>
                )}
            </div>

            <div className="edu-card p-6 mt-4">
                <p className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-3">Currículum (CV)</p>
                <div className="flex flex-wrap gap-2 items-center">
                    {staff.cv_file_id ? (
                        <a href={fileUrl(staff.cv_file_id)} target="_blank" rel="noreferrer" className="px-3 py-2 text-xs rounded-lg border border-edu hover:bg-edu-soft inline-flex items-center gap-1.5">
                            <Eye size={12} /> Ver CV
                        </a>
                    ) : (
                        <span className="text-xs text-edu-muted inline-flex items-center gap-1.5"><FileText size={12} /> Sin CV</span>
                    )}
                    {canEdit && (
                        <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-edu hover:bg-edu-soft cursor-pointer">
                            <Upload size={12} /> {staff.cv_file_id ? "Reemplazar CV" : "Subir CV"}
                            <input type="file" accept=".pdf" className="hidden" onChange={(e) => e.target.files[0] && uploadCV(e.target.files[0])} />
                        </label>
                    )}
                </div>
            </div>

            {canEdit && (
                <div className="flex justify-end mt-6">
                    <button onClick={save} disabled={saving} className="edu-btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                        <Save size={15} /> {saving ? "Guardando…" : "Guardar"}
                    </button>
                </div>
            )}
        </div>
    );
}
